import { motion } from 'motion/react';
import { FaHome, FaHouseUser, FaBuilding, FaWarehouse, FaStoreAlt, FaHotel } from 'react-icons/fa';
import { HiHome, HiOfficeBuilding } from 'react-icons/hi';
import { MdApartment, MdVilla } from 'react-icons/md';

const houseIcons = [
  FaHome,
  FaHouseUser,
  FaBuilding,
  FaWarehouse,
  FaStoreAlt,
  FaHotel,
  HiHome,
  HiOfficeBuilding,
  MdApartment,
  MdVilla,
];

export function AnimatedBackground() {
  // Function to get a random icon with better distribution
  const getRandomIcon = (seed: number) => {
    // Use multiple hash functions for better randomness
    const hash1 = (seed * 9301 + 49297) % 233280;
    const hash2 = (seed * 2147 + 71993) % 199999;
    const hash3 = (seed * 5417 + 13579) % 177777;
    const finalHash = (hash1 + hash2 + hash3) % houseIcons.length;
    return houseIcons[finalHash];
  };

  // Create a larger grid of house icons that loops seamlessly
  const gridItems = Array.from({ length: 600 }, (_, index) => {
    const row = Math.floor(index / 20);
    const col = index % 20;
    
    // Use position-based seed with better prime numbers for more randomness
    const seed = row * 1009 + col * 2017 + index * 4001 + 7919;
    const IconComponent = getRandomIcon(seed);
    
    return {
      id: index,
      Icon: IconComponent,
      x: col * 120 - 60, // Diagonal offset only
      y: row * 120,
      delay: (row + col) * 0.05,
    };
  });

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Animated gradient background - using transform instead of background for smoother animation */}
      <div className="absolute inset-0">
        {/* Gradient 1 - Cyan */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(0, 191, 255, 0.12) 0%, transparent 70%)',
            width: '200%',
            height: '200%',
            left: '-50%',
            top: '-50%',
          }}
          animate={{
            x: ['0%', '15%', '-10%', '0%'],
            y: ['0%', '-12%', '8%', '0%'],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Gradient 2 - Magenta */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(255, 20, 147, 0.12) 0%, transparent 70%)',
            width: '200%',
            height: '200%',
            left: '-50%',
            top: '-50%',
          }}
          animate={{
            x: ['10%', '-8%', '12%', '10%'],
            y: ['-5%', '10%', '-8%', '-5%'],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Gradient 3 - Teal */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(32, 178, 170, 0.12) 0%, transparent 70%)',
            width: '200%',
            height: '200%',
            left: '-50%',
            top: '-50%',
          }}
          animate={{
            x: ['-8%', '10%', '0%', '-8%'],
            y: ['8%', '-5%', '12%', '8%'],
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>
      
      {/* Scrolling house icons grid */}
      <motion.div
        className="absolute inset-0"
        style={{
          transform: 'rotate(-15deg) scale(1.5)',
          transformOrigin: 'center',
        }}
        animate={{
          y: [0, -2400],
        }}
        transition={{
          duration: 80,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
      >
        {gridItems.map((item) => (
          <motion.div
            key={item.id}
            className="absolute"
            style={{
              left: item.x,
              top: item.y,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.06 }}
            transition={{
              delay: item.delay,
              duration: 1,
            }}
          >
            <item.Icon className="w-6 h-6 text-foreground/80" />
          </motion.div>
        ))}
      </motion.div>
      
      {/* Second layer for seamless loop */}
      <motion.div
        className="absolute inset-0"
        style={{
          transform: 'rotate(-15deg) scale(1.5)',
          transformOrigin: 'center',
        }}
        animate={{
          y: [2400, 0],
        }}
        transition={{
          duration: 80,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
      >
        {gridItems.slice(0, 200).map((item) => (
          <motion.div
            key={`second-${item.id}`}
            className="absolute"
            style={{
              left: item.x,
              top: item.y,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.06 }}
            transition={{
              delay: item.delay,
              duration: 1,
            }}
          >
            <item.Icon className="w-6 h-6 text-foreground/80" />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
